import { useState, useEffect } from 'react'
import { Card, Spin, Empty } from 'antd'
import { ClockCircleOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons'
import { getStockGovernance } from '@/api/stock'
import type { StockHolder } from '@/types/stockPool'

function ComingSoon({ title, desc }: { title: string; desc?: string }) {
    return (
        <div className="coming-soon">
            <div className="coming-soon-icon">
                <ClockCircleOutlined style={{ fontSize: 28, color: '#d9d9d9' }} />
            </div>
            <div className="coming-soon-title">{title}</div>
            <div className="coming-soon-desc">{desc ?? '该功能正在建设中，接口就绪后即可上线'}</div>
        </div>
    )
}

function fmtPct(val: number | null): string {
    if (val == null) return '--'
    return val.toFixed(2) + '%'
}

function fmtQty(val: number | null): string {
    if (val == null) return '--'
    if (Math.abs(val) >= 1e8) return (val / 1e8).toFixed(2) + '亿'
    if (Math.abs(val) >= 1e4) return (val / 1e4).toFixed(2) + '万'
    return String(val)
}

interface Props {
    stockCode: string
}

export default function FundTab({ stockCode }: Props) {
    const [holders, setHolders] = useState<StockHolder[]>([])
    const [loading, setLoading] = useState(true)
    const [periodText, setPeriodText] = useState<string>('')

    useEffect(() => {
        if (!stockCode) return
        setHolders([])
        setPeriodText('')
        setLoading(true)
        getStockGovernance(stockCode)
            .then(data => {
                const h = data?.holders ?? []
                setHolders(h)
                if (h.length > 0) setPeriodText(h[0].period_text ?? '')
            })
            .catch(() => setHolders([]))
            .finally(() => setLoading(false))
    }, [stockCode])

    return (
        <div className="new-kb-tab fund-tab">
            <Card
                title={`前十大股东${periodText ? `（${periodText}）` : ''}`}
                size="small"
                className="new-kb-card"
            >
                {loading ? (
                    <div className="chart-placeholder" style={{ height: 100 }}><Spin /></div>
                ) : holders.length === 0 ? (
                    <Empty description="暂无股东数据" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
                ) : (
                    <div className="fin-table-wrap">
                        <table className="fin-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>股东名称</th>
                                    <th>持股数</th>
                                    <th>持股比例</th>
                                    <th>较上期变动</th>
                                    <th>持股日期</th>
                                </tr>
                            </thead>
                            <tbody>
                                {holders.map((h, i) => {
                                    const change = h.holder_quantity_change
                                    return (
                                        <tr key={h.holder_id}>
                                            <td style={{ textAlign: 'center', color: '#999' }}>{i + 1}</td>
                                            <td className="fin-table-label">{h.holder_name ?? '--'}</td>
                                            <td>{fmtQty(h.holder_quantity)}</td>
                                            <td>{fmtPct(h.holder_pct)}</td>
                                            <td style={{
                                                color: change == null ? '#999'
                                                    : change > 0 ? '#ef4444'
                                                    : change < 0 ? '#22c55e'
                                                    : '#999',
                                            }}>
                                                {change == null ? '--' : (
                                                    <>
                                                        {change > 0
                                                            ? <ArrowUpOutlined style={{ marginRight: 2 }} />
                                                            : change < 0
                                                            ? <ArrowDownOutlined style={{ marginRight: 2 }} />
                                                            : null}
                                                        {change < 0 ? '-' : change > 0 ? '+' : ''}{fmtQty(Math.abs(change))}
                                                    </>
                                                )}
                                            </td>
                                            <td>{h.holding_date?.slice(0, 10) ?? '--'}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <Card title="机构持仓趋势" size="small" className="new-kb-card">
                <ComingSoon title="机构持仓趋势即将上线" desc="需接入 fd_institutional_holding 接口，就绪后展示总机构持仓比例历史折线" />
            </Card>

            <Card title="内部人增减持" size="small" className="new-kb-card">
                <ComingSoon title="内部人交易即将上线" desc="需接入 fd_insider_trade 接口，就绪后展示内部人买入/卖出记录" />
            </Card>
        </div>
    )
}
